import {
  Component,
  For,
  JSX,
  Match,
  Show,
  Switch,
  createEffect,
  createResource,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import { Badge, Button, Card } from "../components/ui";
import { useToast } from "../hooks";
import { ApiError } from "../api/client";
import {
  createBillingCheckout,
  createBillingPortal,
  createPackCheckout,
  getTenantBilling,
} from "../api/tenants";
import type {
  BillingInterval,
  BillingPlan,
  TenantBillingStatus,
  TenantId,
  TokenPack,
} from "../api/types";
import { currentUser } from "../stores/currentUserStore";
import {
  TOKEN_PACKS,
  classifyBillingError,
  currentPlan,
  formatPeriodDate,
  formatTokens,
  isLiveSubscription,
  periodDateLabel,
  storePackBaseline,
  subscriptionBadgeVariant,
  subscriptionStatusLabel,
  takePackBaseline,
  trialState,
} from "../utils/billing";
import { PLAN_COPY, TRIAL_COPY } from "../content/plans";

const INTERVAL_OPTIONS: { value: BillingInterval; label: string }[] = [
  { value: "month", label: "Monthly" },
  { value: "year", label: "Annual" },
];

const INTERVAL_SUMMARY: Record<BillingInterval, string> = {
  month: "Billed monthly",
  year: "Billed annually",
};

/**
 * The subscription is being activated, or a token pack credited, by a webhook
 * we cannot wait on directly (FLOW-700, FLOW-851).
 */
const ACTIVATION_POLL_MS = 3_000;
const ACTIVATION_LIMIT_MS = 60_000;

const httpStatus = (err: unknown): number | undefined =>
  err instanceof ApiError ? err.status : undefined;

const errorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : "Failed to load billing.";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `?tenant=` comes from a link anyone can hand the user and ends up
 * interpolated into an API path, so only a well-formed UUID is honoured. A
 * mangled value is discarded rather than reported: landing on the user's own
 * billing is a better answer than an error screen.
 */
const explicitTenant = (raw: string | undefined): TenantId | undefined =>
  raw !== undefined && UUID_RE.test(raw) ? raw : undefined;

/**
 * `plan` and `billing_interval` are read defensively rather than trusted.
 * Both are `null` when the subscription's Price sits outside the configured
 * catalogue, and indexing these tables with a missing value would throw during
 * render — which the route's ErrorBoundary turns into an error screen for a
 * tenant whose subscription is perfectly fine. A subscription with no plan we
 * recognise still renders; it just says less.
 */
const planName = (plan: BillingPlan | null | undefined): string =>
  (plan == null ? undefined : PLAN_COPY[plan])?.name ?? "Subscription";

const intervalSummary = (
  interval: BillingInterval | null | undefined,
): string | undefined =>
  interval == null ? undefined : INTERVAL_SUMMARY[interval];

const intervalChipClass = (selected: boolean) =>
  "inline-flex items-center justify-center h-7 px-3 text-sm font-medium rounded border transition-colors focus-ring " +
  (selected
    ? "bg-ink text-paper border-ink"
    : "bg-surface text-ink-2 border-line hover:bg-sunken hover:text-ink");

const NoticeCard: Component<{ children: string }> = (props) => (
  <Card class="p-5 text-sm text-ink-2">{props.children}</Card>
);

const InlineError: Component<{ children: string }> = (props) => (
  <p role="alert" class="text-sm text-signal-red">
    {props.children}
  </p>
);

const CurrentPlanBadge: Component = () => (
  <Badge variant="inProgress">Current plan</Badge>
);

/**
 * The free trial every new organization starts on, shown only to a tenant with
 * no live subscription — a subscriber's trial is over whatever the date says.
 *
 * The backend does not enforce expiry, so an elapsed trial still says when it
 * ended rather than claiming anything about what the tenant can still do.
 */
const TrialCard: Component<{ billing: TenantBillingStatus }> = (props) => {
  const state = () => trialState(props.billing.trial_ends_at);
  return (
    <Card
      variant={state() === "active" ? "elevated" : "default"}
      class="flex flex-col gap-3 p-5"
    >
      <div class="flex items-center justify-between gap-3">
        <h2 class="text-base font-semibold text-ink">{TRIAL_COPY.name}</h2>
        <Show when={state() === "active"}>
          <CurrentPlanBadge />
        </Show>
      </div>
      <div class="text-lg font-semibold text-ink">{TRIAL_COPY.price}</div>
      <p class="flex-1 text-sm text-ink-2">{TRIAL_COPY.note}</p>
      <Show when={formatPeriodDate(props.billing.trial_ends_at)}>
        {(date) => (
          <p class="text-sm text-ink-2">
            {(state() === "active" ? "Ends on " : "Ended on ") + date()}
          </p>
        )}
      </Show>
    </Card>
  );
};

/**
 * One purchasable plan. The footer is the only part that differs by tenant:
 * the plan it is on shows its subscription instead of a buy button, and a
 * subscriber's other plans say why they cannot be bought — the backend has no
 * route that switches a live subscription's plan (FLOW-925 D1).
 */
const PlanCard: Component<{
  plan: BillingPlan;
  interval: BillingInterval;
  busy: boolean;
  current: boolean;
  live: boolean;
  children?: JSX.Element;
  onSubscribe: (plan: BillingPlan) => void;
}> = (props) => (
  <Card
    variant={props.current ? "elevated" : "default"}
    class="flex flex-col gap-3 p-5"
  >
    <div class="flex items-center justify-between gap-3">
      <h2 class="text-base font-semibold text-ink">
        {PLAN_COPY[props.plan].name}
      </h2>
      <Show when={props.current}>
        <CurrentPlanBadge />
      </Show>
    </div>
    <div class="text-lg font-semibold text-ink">
      {PLAN_COPY[props.plan].price[props.interval]}
    </div>
    <p class="flex-1 text-sm text-ink-2">{PLAN_COPY[props.plan].blurb}</p>
    <Switch
      fallback={
        <Button
          variant="primary"
          disabled={props.busy}
          onClick={() => props.onSubscribe(props.plan)}
        >
          {"Subscribe to " + PLAN_COPY[props.plan].name}
        </Button>
      }
    >
      <Match when={props.current}>{props.children}</Match>
      <Match when={props.live}>
        <p class="text-sm text-ink-3">Switching plans is not self-serve yet.</p>
      </Match>
    </Switch>
  </Card>
);

/**
 * Every plan, with the tenant's own marked current (FLOW-925).
 *
 * The Trial card is shown only while no subscription is live (D2). A live
 * tenant sees no interval toggle and no Subscribe buttons: the current card
 * holds the subscription details, the others say switching is not
 * self-serve (D1), and prices follow the tenant's own interval (D5). A
 * non-current Personal card is hidden from an org with more than one admin,
 * since Personal is one seat; the current one is never hidden.
 *
 * A non-null-but-not-live `subscription_status` (`canceled`, `incomplete`)
 * gets a one-line strip above the cards, which is the only place "Ends on"
 * for a subscription can appear.
 */
const PlanGrid: Component<{
  billing: TenantBillingStatus;
  interval: BillingInterval;
  busy: boolean;
  error: string;
  onIntervalChange: (interval: BillingInterval) => void;
  onSubscribe: (plan: BillingPlan) => void;
  portalPending: boolean;
  portalError: string;
  packBusy: boolean;
  packError: string;
  onManage: () => void;
  onBuyPack: (pack: TokenPack) => void;
}> = (props) => {
  const live = () => isLiveSubscription(props.billing.subscription_status);
  const current = () => currentPlan(props.billing);
  const interval = () =>
    live() ? (props.billing.billing_interval ?? "month") : props.interval;
  const showPersonal = () =>
    props.billing.seat_holders <= 1 || current() === "personal";
  // Safe to hand to both PlanCards as `children` only because Solid compiles
  // this call into a lazy `get children()` and PlanCard reads `props.children`
  // exactly once, inside `<Match when={props.current}>` — reading it a second
  // time there would create a second Manage-billing button and packs section.
  const details = () => (
    <SubscriptionDetails
      billing={props.billing}
      portalPending={props.portalPending}
      portalError={props.portalError}
      packBusy={props.packBusy}
      packError={props.packError}
      onManage={props.onManage}
      onBuyPack={props.onBuyPack}
    />
  );

  return (
    <div class="flex flex-col gap-4">
      <Show when={!live() && props.billing.subscription_status}>
        {(prior) => (
          <div class="flex flex-wrap items-center gap-3">
            <Badge variant={subscriptionBadgeVariant(prior())}>
              {subscriptionStatusLabel(prior())}
            </Badge>
            <Show when={formatPeriodDate(props.billing.current_period_end)}>
              {(date) => (
                <span class="text-sm text-ink-2">
                  {periodDateLabel(prior()) + " " + date()}
                </span>
              )}
            </Show>
          </div>
        )}
      </Show>

      <Show when={!live()}>
        <div
          role="group"
          aria-label="Billing interval"
          class="flex items-center gap-1.5"
        >
          <For each={INTERVAL_OPTIONS}>
            {(option) => (
              <button
                type="button"
                aria-pressed={props.interval === option.value}
                onClick={() => props.onIntervalChange(option.value)}
                class={intervalChipClass(props.interval === option.value)}
              >
                {option.label}
              </button>
            )}
          </For>
        </div>
      </Show>

      <Show when={!showPersonal()}>
        <p class="text-sm text-ink-3">
          {"The Personal plan is not shown: it includes one seat, and your organization has " +
            props.billing.seat_holders +
            " admins."}
        </p>
      </Show>

      <div
        class={"grid gap-4 sm:grid-cols-2" + (live() ? "" : " lg:grid-cols-3")}
      >
        <Show when={!live()}>
          <TrialCard billing={props.billing} />
        </Show>
        <Show when={showPersonal()}>
          <PlanCard
            plan="personal"
            interval={interval()}
            busy={props.busy}
            current={current() === "personal"}
            live={live()}
            onSubscribe={props.onSubscribe}
          >
            {details()}
          </PlanCard>
        </Show>
        <PlanCard
          plan="pro"
          interval={interval()}
          busy={props.busy}
          current={current() === "pro"}
          live={live()}
          onSubscribe={props.onSubscribe}
        >
          {details()}
        </PlanCard>
      </div>

      <Show when={props.error}>
        {(message) => <InlineError>{message()}</InlineError>}
      </Show>
    </div>
  );
};

/**
 * Add-on token packs (FLOW-851). Only ever rendered inside the subscription
 * card: a pack tops up a monthly cap the plan sets, so there is nothing to top
 * up without a live subscription (D5).
 *
 * The credit is scoped to the UTC month it was paid in (D1), which the copy
 * states rather than leaving the user to discover on the 1st.
 */
const TokenPacksSection: Component<{
  billing: TenantBillingStatus;
  busy: boolean;
  error: string;
  onBuy: (pack: TokenPack) => void;
}> = (props) => (
  <div class="flex w-full flex-col items-start gap-2 border-t border-line pt-4">
    <h3 class="text-sm font-semibold text-ink">Add-on token packs</h3>

    <Show when={props.billing.pack_tokens_this_month > 0}>
      <p class="text-sm text-ink-2">
        {"Added this month: " +
          formatTokens(props.billing.pack_tokens_this_month)}
      </p>
    </Show>

    <p class="text-sm text-ink-3">
      Packs top up this month's AI budget and reset on the 1st.
    </p>

    <div class="flex flex-wrap items-center gap-2">
      <For each={TOKEN_PACKS}>
        {(option) => (
          <Button
            variant="secondary"
            disabled={props.busy}
            onClick={() => props.onBuy(option.pack)}
          >
            {"Buy " + option.label + " · " + option.price}
          </Button>
        )}
      </For>
    </div>

    <Show when={props.error}>
      {(message) => <InlineError>{message()}</InlineError>}
    </Show>
  </div>
);

/**
 * Everything a live subscription shows below its plan name: status, interval,
 * renewal date, seats, Manage billing and the token packs. Rendered inside
 * the current plan's card by PlanGrid, or inside SubscriptionCard when the
 * plan is unknown.
 */
const SubscriptionDetails: Component<{
  billing: TenantBillingStatus;
  portalPending: boolean;
  portalError: string;
  packBusy: boolean;
  packError: string;
  onManage: () => void;
  onBuyPack: (pack: TokenPack) => void;
}> = (props) => (
  <div class="flex w-full flex-col items-start gap-3">
    <Badge
      variant={subscriptionBadgeVariant(props.billing.subscription_status)}
    >
      {subscriptionStatusLabel(props.billing.subscription_status)}
    </Badge>

    <Show when={intervalSummary(props.billing.billing_interval)}>
      {(summary) => <p class="text-sm text-ink-2">{summary()}</p>}
    </Show>

    <Show when={formatPeriodDate(props.billing.current_period_end)}>
      {(date) => (
        <p class="text-sm text-ink-2">
          {periodDateLabel(props.billing.subscription_status) + " " + date()}
        </p>
      )}
    </Show>

    <Show when={props.billing.plan === "pro"}>
      <p class="text-sm text-ink-2">
        {"Seats: " +
          props.billing.seats_in_use +
          " in use · " +
          (props.billing.seats_purchased ?? "—") +
          " purchased"}
      </p>
    </Show>

    <Button
      variant="secondary"
      loading={props.portalPending}
      onClick={props.onManage}
    >
      Manage billing
    </Button>

    <Show when={props.portalError}>
      {(message) => <InlineError>{message()}</InlineError>}
    </Show>

    <TokenPacksSection
      billing={props.billing}
      busy={props.packBusy}
      error={props.packError}
      onBuy={props.onBuyPack}
    />
  </div>
);

/**
 * A live subscription whose Price is outside the configured catalogue, so
 * there is no plan to mark current (D3). Says nothing about a pending
 * cancellation: the payload carries no `cancel_at_period_end`.
 */
const SubscriptionCard: Component<{
  billing: TenantBillingStatus;
  portalPending: boolean;
  portalError: string;
  packBusy: boolean;
  packError: string;
  onManage: () => void;
  onBuyPack: (pack: TokenPack) => void;
}> = (props) => (
  <Card class="flex flex-col items-start gap-3 p-5">
    <h2 class="text-base font-semibold text-ink">
      {planName(props.billing.plan)}
    </h2>
    <SubscriptionDetails {...props} />
  </Card>
);

/**
 * Org-level billing (FLOW-700).
 *
 * Reads the caller's primary tenant from the `/auth/me` payload AppShell
 * already published, or an explicit `?tenant=`. The backend is the authority
 * on who may see this: a non-admin gets a 403 and the read-only card, rather
 * than the page pre-judging from `tenant_role`.
 *
 * Returning from Stripe Checkout lands on `?checkout=success` before the
 * `checkout.session.completed` webhook has necessarily been processed, so the
 * page polls the status for up to a minute rather than claiming a
 * subscription that is not recorded yet. `?checkout=pack` is the same race for
 * an add-on token pack (FLOW-851) and polls the same way, watching the month's
 * credited total instead of the subscription status.
 */
export const BillingPage: Component = () => {
  const [searchParams, setSearchParams] = useSearchParams<{
    tenant?: string;
    checkout?: string;
  }>();
  const { showInfo, showSuccess } = useToast();

  const tenantId = (): TenantId | undefined =>
    explicitTenant(searchParams.tenant) ??
    currentUser()?.tenants?.[0]?.tenant_id;

  // Wrapped rather than passed by reference: createResource hands its fetcher a
  // second `ResourceFetcherInfo` argument, which has no business reaching the
  // API client.
  const [status, { refetch }] = createResource(tenantId, (id) =>
    getTenantBilling(id),
  );

  // A 503 from checkout or portal means this deployment has no Stripe at all,
  // which outranks whatever the status read returned.
  const [noStripe, setNoStripe] = createSignal(false);
  const [billingInterval, setBillingInterval] =
    createSignal<BillingInterval>("month");
  const [pendingPlan, setPendingPlan] = createSignal<BillingPlan | null>(null);
  const [pickerError, setPickerError] = createSignal("");
  const [portalPending, setPortalPending] = createSignal(false);
  const [portalError, setPortalError] = createSignal("");
  const [activating, setActivating] = createSignal(false);
  const [activationStalled, setActivationStalled] = createSignal(false);
  const [pendingPack, setPendingPack] = createSignal<TokenPack | null>(null);
  const [packError, setPackError] = createSignal("");
  const [crediting, setCrediting] = createSignal(false);
  const [creditStalled, setCreditStalled] = createSignal(false);

  let disposed = false;
  let pollTimer: ReturnType<typeof setInterval> | undefined;
  // The month's pack total to beat. Only growth proves *this* purchase was
  // credited: a tenant that bought a pack earlier in the month starts from
  // that figure, not from zero. Taken from `sessionStorage` when the trip to
  // Stripe left one, else from the first payload the poll reads.
  let packBaseline: number | undefined;

  const stopPolling = () => {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  onCleanup(() => {
    disposed = true;
    stopPolling();
  });

  /** Re-reads the status until `giveUp` is reached; the caller stops it on success. */
  const startPolling = (giveUp: () => void) => {
    const startedAt = Date.now();
    pollTimer = setInterval(() => {
      if (Date.now() - startedAt >= ACTIVATION_LIMIT_MS) {
        stopPolling();
        giveUp();
        return;
      }
      // A read slower than the interval would otherwise stack overlapping
      // requests for the rest of the minute.
      if (status.loading) return;
      void refetch();
    }, ACTIVATION_POLL_MS);
  };

  onMount(() => {
    // Read once and clear: left in the URL, a reload or a Back to this entry
    // would re-arm the poll and toast a subscription that has been live for
    // days. Everything below runs off the local copy.
    const outcome = searchParams.checkout;
    if (outcome !== undefined) {
      setSearchParams({ checkout: undefined }, { replace: true });
    }

    if (outcome === "cancelled") {
      showInfo("Checkout cancelled");
      return;
    }
    if (outcome === "success") {
      setActivating(true);
      startPolling(() => {
        setActivating(false);
        setActivationStalled(true);
      });
      return;
    }
    if (outcome === "pack") {
      packBaseline = takePackBaseline();
      setCrediting(true);
      startPolling(() => {
        setCrediting(false);
        setCreditStalled(true);
      });
    }
  });

  /** Reading the resource is only safe once it holds a value, never while errored. */
  const loaded = (): TenantBillingStatus | undefined =>
    status.state === "ready" || status.state === "refreshing"
      ? status()
      : undefined;

  createEffect(() => {
    if (!activating()) return;
    if (!isLiveSubscription(loaded()?.subscription_status)) return;
    stopPolling();
    setActivating(false);
    showSuccess("Subscription activated");
  });

  createEffect(() => {
    if (!crediting()) return;
    const credited = loaded()?.pack_tokens_this_month;
    if (credited === undefined) return;
    if (packBaseline === undefined) {
      packBaseline = credited;
      return;
    }
    if (credited <= packBaseline) return;
    stopPolling();
    setCrediting(false);
    showSuccess("Token pack added");
  });

  const unavailable = () => noStripe() || httpStatus(status.error) === 503;
  const forbidden = () => httpStatus(status.error) === 403;
  // currentUser() is published by AppShell's auth gate before any routed page
  // renders; treat the gap as loading rather than as "you have no org".
  const resolvingUser = () =>
    currentUser() === null && tenantId() === undefined;

  const handleFailure = (err: unknown, setMessage: (m: string) => void) => {
    const outcome = classifyBillingError(err);
    if (outcome.kind === "unavailable") {
      setNoStripe(true);
      return;
    }
    if (outcome.kind === "stale") {
      void refetch();
      return;
    }
    setMessage(outcome.message);
  };

  const subscribe = async (plan: BillingPlan) => {
    const tenant = tenantId();
    const interval = billingInterval();
    if (!tenant) return;
    setPickerError("");
    setPendingPlan(plan);
    try {
      const session = await createBillingCheckout(tenant, { plan, interval });
      // The user may have navigated away during the round trip; sending them
      // to Stripe from a page they have left is worse than doing nothing.
      if (disposed) return;
      window.location.assign(session.url);
    } catch (err) {
      if (disposed) return;
      handleFailure(err, setPickerError);
    } finally {
      if (!disposed) setPendingPlan(null);
    }
  };

  /**
   * A 503 from the packs route says the two pack Prices are unconfigured
   * (FLOW-851 D7), which is every deployment's state until they reach Railway
   * — the plan catalogue, the portal and this card all still work. So it is
   * reported beside the buttons rather than through `handleFailure`, which
   * would flip `noStripe` and replace the whole surface.
   */
  const handlePackFailure = (err: unknown) => {
    if (classifyBillingError(err).kind === "unavailable") {
      setPackError("Token packs are not available in this environment.");
      return;
    }
    handleFailure(err, setPackError);
  };

  const buyPack = async (pack: TokenPack) => {
    const tenant = tenantId();
    if (!tenant) return;
    setPackError("");
    setPendingPack(pack);
    try {
      const session = await createPackCheckout(tenant, { pack });
      if (disposed) return;
      storePackBaseline(loaded()?.pack_tokens_this_month ?? 0);
      window.location.assign(session.url);
    } catch (err) {
      if (disposed) return;
      handlePackFailure(err);
    } finally {
      if (!disposed) setPendingPack(null);
    }
  };

  const openPortal = async () => {
    const tenant = tenantId();
    if (!tenant) return;
    setPortalError("");
    setPortalPending(true);
    try {
      const session = await createBillingPortal(tenant);
      if (disposed) return;
      window.location.assign(session.url);
    } catch (err) {
      if (disposed) return;
      handleFailure(err, setPortalError);
    } finally {
      if (!disposed) setPortalPending(false);
    }
  };

  return (
    <div class="h-full overflow-auto">
      <div class="max-w-4xl mx-auto px-6 py-8">
        <div class="mb-6">
          <h1 class="text-2xl font-bold text-ink">Billing</h1>
          <p class="text-sm text-ink-3 mt-1">
            Your organization's subscription and seats
          </p>
        </div>

        <div role="status" aria-live="polite">
          <Show when={activating()}>
            <div class="mb-6 rounded border border-signal-blue/25 bg-signal-blue-tint px-4 py-3 text-sm text-signal-blue">
              Activating your subscription…
            </div>
          </Show>
          <Show when={activationStalled()}>
            <div class="mb-6 rounded border border-signal-amber/25 bg-signal-amber-tint px-4 py-3 text-sm text-signal-amber">
              Still activating — refresh in a moment.
            </div>
          </Show>
          <Show when={crediting()}>
            <div class="mb-6 rounded border border-signal-blue/25 bg-signal-blue-tint px-4 py-3 text-sm text-signal-blue">
              Adding your token pack…
            </div>
          </Show>
          <Show when={creditStalled()}>
            <div class="mb-6 rounded border border-signal-amber/25 bg-signal-amber-tint px-4 py-3 text-sm text-signal-amber">
              Still processing — refresh in a moment.
            </div>
          </Show>
        </div>

        <Switch
          fallback={
            <NoticeCard>No organization is linked to your account.</NoticeCard>
          }
        >
          <Match when={unavailable()}>
            <NoticeCard>
              Billing is not available in this environment.
            </NoticeCard>
          </Match>
          <Match when={forbidden()}>
            <NoticeCard>
              Billing is managed by your organization admin.
            </NoticeCard>
          </Match>
          <Match when={status.state === "pending" || resolvingUser()}>
            <NoticeCard>Loading billing…</NoticeCard>
          </Match>
          <Match when={status.error}>
            {(err) => (
              <Card class="flex flex-col items-start gap-3 p-5">
                <InlineError>{errorMessage(err())}</InlineError>
                <Button variant="secondary" onClick={() => void refetch()}>
                  Retry
                </Button>
              </Card>
            )}
          </Match>
          <Match when={loaded()}>
            {(billing) => (
              <Show
                when={
                  isLiveSubscription(billing().subscription_status) &&
                  billing().plan == null
                }
                fallback={
                  <PlanGrid
                    billing={billing()}
                    interval={billingInterval()}
                    busy={pendingPlan() !== null || activating()}
                    error={pickerError()}
                    onIntervalChange={setBillingInterval}
                    onSubscribe={(plan) => void subscribe(plan)}
                    portalPending={portalPending()}
                    portalError={portalError()}
                    packBusy={pendingPack() !== null}
                    packError={packError()}
                    onManage={() => void openPortal()}
                    onBuyPack={(pack) => void buyPack(pack)}
                  />
                }
              >
                <SubscriptionCard
                  billing={billing()}
                  portalPending={portalPending()}
                  portalError={portalError()}
                  packBusy={pendingPack() !== null}
                  packError={packError()}
                  onManage={() => void openPortal()}
                  onBuyPack={(pack) => void buyPack(pack)}
                />
              </Show>
            )}
          </Match>
        </Switch>
      </div>
    </div>
  );
};
